import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Drawer,
  IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../theme';

const API_BASE = 'https://filtrd-prod-f09ee9687166.herokuapp.com/api';
const STORAGE_KEY = 'admin_api_key';

interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
}

interface XFollow {
  id: string;
  username: string;
  name?: string;
  profileImageUrl?: string;
  verified: boolean;
  verifiedType?: string;
  followersCount?: number;
  UserXFollow?: {
    categoryId?: string;
  };
}

interface User {
  id: string;
  email: string;
  name: string;
  slug: string;
  cadence: string;
  status: string;
  prompt?: string;
  onboardingId?: string;
  createdAt?: string;
  updatedAt?: string;
  categories?: Category[];
  xFollows?: XFollow[];
}

const AdminUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const headers = useCallback(() => ({
    'X-API-Key': apiKey,
    'X-User-Id': 'admin',
    'X-App-Name': 'Filtrd',
    'X-Roles': 'Meta Admin',
  }), [apiKey]);

  const fetchUsers = useCallback(async () => {
    if (!apiKey) return;
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/users?limit=50&include=categories,xFollows`, {
        headers: headers(),
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Invalid API key');
        }
        throw new Error(`Failed to fetch: ${response.statusText}`);
      }

      const data = await response.json();
      const items = data?.data?.items || [];
      setUsers(Array.isArray(items) ? items : []);
      setIsAuthenticated(true);
      localStorage.setItem(STORAGE_KEY, apiKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, [apiKey, headers]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    fetchUsers();
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUsers([]);
    localStorage.removeItem(STORAGE_KEY);
    setApiKey('');
  };

  useEffect(() => {
    if (apiKey && !isAuthenticated) {
      fetchUsers();
    }
  }, [apiKey, isAuthenticated, fetchUsers]);

  const handleUserClick = (user: User) => {
    setSelectedUser(user);
    setDrawerOpen(true);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatNumber = (num?: number) => {
    if (num === undefined || num === null) return '-';
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
    return num.toString();
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'active': return colors.success;
      case 'paused': return colors.signalOrange;
      case 'suspended': return colors.error;
      default: return colors.slate[400];
    }
  };

  const cadenceLabel = (cadence: string) => {
    switch (cadence) {
      case 'daily': return 'Daily';
      case '3-day': return 'Every 3 days';
      case 'weekly': return 'Weekly';
      default: return cadence;
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: colors.deepNavy,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          sx={{
            p: 4,
            maxWidth: 400,
            width: '100%',
            backgroundColor: colors.whiteAlpha[10],
            border: colors.borders.whiteAlpha10,
          }}
        >
          <Typography variant="h5" sx={{ color: colors.pureWhite, mb: 3, textAlign: 'center' }}>
            Admin - Users
          </Typography>
          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              type="password"
              label="API Key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  color: colors.pureWhite,
                  '& fieldset': { borderColor: colors.whiteAlpha[20] },
                  '&:hover fieldset': { borderColor: colors.whiteAlpha[30] },
                },
                '& .MuiInputLabel-root': { color: colors.slate[400] },
              }}
            />
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading || !apiKey}
              sx={{
                backgroundColor: colors.signalOrange,
                '&:hover': { backgroundColor: colors.signalOrangeHover },
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Login'}
            </Button>
          </form>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: colors.deepNavy, py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/admin')}
              sx={{ color: colors.slate[400], '&:hover': { color: colors.pureWhite } }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ color: colors.pureWhite }}>
              Users ({users.length})
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => navigate('/admin')}
              sx={{
                color: colors.signalOrange,
                borderColor: colors.signalOrange,
                '&:hover': {
                  borderColor: colors.signalOrangeHover,
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Onboardings
            </Button>
            <Button
              onClick={() => navigate('/admin/x-users')}
              sx={{
                color: colors.signalOrange,
                borderColor: colors.signalOrange,
                '&:hover': {
                  borderColor: colors.signalOrangeHover,
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              X Users
            </Button>
            <Button
              onClick={() => navigate('/admin/categories')}
              sx={{
                color: colors.signalOrange,
                borderColor: colors.signalOrange,
                '&:hover': {
                  borderColor: colors.signalOrangeHover,
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Categories
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchUsers}
              disabled={loading}
              sx={{
                color: colors.pureWhite,
                borderColor: colors.whiteAlpha[30],
                '&:hover': {
                  borderColor: colors.pureWhite,
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              sx={{
                color: colors.error,
                borderColor: colors.error,
                '&:hover': {
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Logout
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: colors.signalOrange }} />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: colors.whiteAlpha[10],
              border: colors.borders.whiteAlpha10,
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Cadence</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Categories</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>X Follows</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Prompt</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow
                    key={user.id}
                    hover
                    onClick={() => handleUserClick(user)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: colors.whiteAlpha[10] },
                    }}
                  >
                    <TableCell sx={{ color: colors.pureWhite, fontWeight: 500 }}>
                      {user.name}
                      <Typography variant="caption" sx={{ display: 'block', color: colors.slate[400] }}>
                        /{user.slug}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: colors.slate[400], fontSize: '0.85rem' }}>
                      {user.email}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.status}
                        size="small"
                        sx={{
                          backgroundColor: `${statusColor(user.status)}22`,
                          color: statusColor(user.status),
                          fontWeight: 500,
                          textTransform: 'capitalize',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: colors.pureWhite }}>
                      {cadenceLabel(user.cadence)}
                    </TableCell>
                    <TableCell>
                      {user.categories && user.categories.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {user.categories.map((cat) => (
                            <Chip
                              key={cat.id}
                              label={cat.name}
                              size="small"
                              sx={{
                                backgroundColor: colors.whiteAlpha[10],
                                color: colors.slate[400],
                                fontSize: '0.75rem',
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" sx={{ color: colors.slate[500] }}>
                          None
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: colors.pureWhite }}>
                      {user.xFollows ? user.xFollows.length : 0}
                    </TableCell>
                    <TableCell>
                      {user.prompt ? (
                        <Chip
                          label={`${(user.prompt.length / 1000).toFixed(1)}K chars`}
                          size="small"
                          sx={{
                            backgroundColor: `${colors.success}22`,
                            color: colors.success,
                            fontSize: '0.75rem',
                          }}
                        />
                      ) : (
                        <Typography variant="caption" sx={{ color: colors.slate[500] }}>
                          None
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: colors.slate[400], fontSize: '0.85rem' }}>
                      {formatDate(user.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      {/* User Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', md: 700 },
            backgroundColor: colors.deepNavy,
            borderLeft: colors.borders.whiteAlpha10,
          },
        }}
      >
        {selectedUser && (
          <Box sx={{ p: 3 }}>
            {/* Drawer Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ color: colors.pureWhite }}>
                {selectedUser.name}
              </Typography>
              <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: colors.slate[400] }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* User Info */}
            <Box sx={{ mb: 3 }}>
              {[
                ['Email', selectedUser.email],
                ['Slug', `/${selectedUser.slug}`],
                ['Status', selectedUser.status],
                ['Cadence', cadenceLabel(selectedUser.cadence)],
                ['Created', formatDate(selectedUser.createdAt)],
                ['Updated', formatDate(selectedUser.updatedAt)],
                ['Onboarding ID', selectedUser.onboardingId || 'None'],
              ].map(([label, value]) => (
                <Box key={label as string} sx={{ display: 'flex', py: 0.75, borderBottom: `1px solid ${colors.whiteAlpha[10]}` }}>
                  <Typography sx={{ color: colors.slate[400], width: 140, flexShrink: 0, fontSize: '0.9rem' }}>
                    {label}
                  </Typography>
                  <Typography sx={{ color: colors.pureWhite, fontSize: '0.9rem' }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Categories */}
            <Typography variant="h6" sx={{ color: colors.pureWhite, mb: 1 }}>
              Categories ({selectedUser.categories?.length || 0})
            </Typography>
            {selectedUser.categories && selectedUser.categories.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {selectedUser.categories.map((cat) => (
                  <Chip
                    key={cat.id}
                    label={cat.name}
                    sx={{
                      backgroundColor: colors.whiteAlpha[10],
                      color: colors.pureWhite,
                      border: colors.borders.whiteAlpha10,
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Typography sx={{ color: colors.slate[500], mb: 3, fontSize: '0.9rem' }}>
                No categories assigned
              </Typography>
            )}

            {/* X Follows */}
            <Typography variant="h6" sx={{ color: colors.pureWhite, mb: 1 }}>
              X Follows ({selectedUser.xFollows?.length || 0})
            </Typography>
            {selectedUser.xFollows && selectedUser.xFollows.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                {selectedUser.xFollows.map((xUser) => (
                  <Box
                    key={xUser.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: colors.whiteAlpha[10],
                      border: colors.borders.whiteAlpha10,
                    }}
                  >
                    {xUser.profileImageUrl && (
                      <img
                        src={xUser.profileImageUrl}
                        alt={xUser.username}
                        style={{ width: 36, height: 36, borderRadius: '50%' }}
                      />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: colors.pureWhite, fontSize: '0.9rem', fontWeight: 500 }}>
                        {xUser.name || xUser.username}
                        {xUser.verified && (
                          <Chip
                            label={xUser.verifiedType || 'verified'}
                            size="small"
                            sx={{
                              ml: 1,
                              height: 18,
                              fontSize: '0.65rem',
                              backgroundColor: `${colors.signalOrange}22`,
                              color: colors.signalOrange,
                            }}
                          />
                        )}
                      </Typography>
                      <Typography sx={{ color: colors.slate[400], fontSize: '0.8rem' }}>
                        @{xUser.username}
                        {xUser.followersCount !== undefined && (
                          <> &middot; {formatNumber(xUser.followersCount)} followers</>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ color: colors.slate[500], mb: 3, fontSize: '0.9rem' }}>
                No X follows assigned
              </Typography>
            )}

            {/* Prompt Preview */}
            <Typography variant="h6" sx={{ color: colors.pureWhite, mb: 1 }}>
              Prompt {selectedUser.prompt ? `(${(selectedUser.prompt.length / 1000).toFixed(1)}K chars)` : ''}
            </Typography>
            {selectedUser.prompt ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: colors.whiteAlpha[10],
                  border: colors.borders.whiteAlpha10,
                  maxHeight: 400,
                  overflow: 'auto',
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    color: colors.slate[400],
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    m: 0,
                  }}
                >
                  {selectedUser.prompt}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ color: colors.slate[500], fontSize: '0.9rem' }}>
                No prompt configured
              </Typography>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default AdminUsersPage;
